import React, { useEffect, useMemo, useState } from "react";
import {
  FiAward,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiGift,
  FiPackage,
  FiPercent,
  FiRefreshCw,
  FiRotateCcw,
  FiShoppingCart,
  FiTruck,
  FiXCircle,
} from "react-icons/fi";
import Breadcrumbs from "../components/Breadcrumbs";
import { useCustomer } from "../context/CustomerContext";
import { getLoyaltySettings, getMyLoyalty } from "../api/loyaltyApi";
import { formatKopecks, LOYALTY_MAINTENANCE_MESSAGE, normalizeLoyaltySettings } from "../utils/loyalty";
import { useLoyaltySettings } from "../context/LoyaltySettingsContext";
import styles from "./BonusProgram.module.css";

const DEMO_BALANCE_KOPECKS = 14850;
const percentFormatter = new Intl.NumberFormat("uk-UA", {
  maximumFractionDigits: 4,
});

const formatPercent = (value) =>
  `${percentFormatter.format(Math.max(0, Number(value) || 0))}%`;

const exampleBonus = (kopecks, percent) =>
  Math.round(
    (kopecks * Math.max(0, Math.round((Number(percent) || 0) * 100))) /
      10000,
  );

const formatMonths = (value) => {
  const count = Math.max(0, Math.trunc(Number(value) || 0));
  const lastTwo = count % 100;
  const last = count % 10;
  const word =
    lastTwo >= 11 && lastTwo <= 14
      ? "місяців"
      : last === 1
        ? "місяць"
        : last >= 2 && last <= 4
          ? "місяці"
          : "місяців";
  return `${count} ${word}`;
};

const ruleView = (settings, key) => {
  const rule = settings?.[key] || {};
  const multiplier = Math.max(0, Number(settings?.defaultMultiplier) || 1);
  const enabled = Boolean(settings?.enabled && rule.enabled);
  return {
    enabled,
    percent: enabled
      ? Math.max(0, Number(rule.ratePercent) || 0) * multiplier
      : 0,
  };
};

const SectionHead = ({ Icon, id, title, children }) => (
  <div className={styles.sectionHead}>
    <span className={styles.sectionIcon} aria-hidden="true">
      <Icon />
    </span>
    <div>
      <h2 id={id}>{title}</h2>
      {children && <p>{children}</p>}
    </div>
  </div>
);

const BonusProgram = () => {
  const { settings: sharedSettings } = useLoyaltySettings();
  const customerContext = useCustomer() || {};
  const { customer, loading: customerLoading = false } = customerContext;
  const customerId = customer?._id || customer?.id || null;
  const isAuthenticated = Boolean(customerId);

  const [loadedSettings, setSettings] = useState(null);
  const settings = sharedSettings || loadedSettings;
  const [state, setState] = useState({ loading: true, error: "" });
  const [retry, setRetry] = useState(0);
  const [balanceState, setBalanceState] = useState({
    loading: false,
    error: "",
    summary: null,
  });

  useEffect(() => {
    const controller = new AbortController();
    let mounted = true;
    setState({ loading: true, error: "" });

    getLoyaltySettings({ signal: controller.signal })
      .then((data) => {
        if (mounted) setSettings(normalizeLoyaltySettings(data));
      })
      .catch((error) => {
        if (mounted && error?.code !== "ERR_CANCELED") {
          setState({
            loading: false,
            error:
              "Не вдалося завантажити актуальні умови бонусної програми.",
          });
        }
      })
      .finally(() => {
        if (mounted) {
          setState((current) => ({ ...current, loading: false }));
        }
      });

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [retry]);

  useEffect(() => {
    if (customerLoading) return undefined;

    if (!customerId) {
      setBalanceState({ loading: false, error: "", summary: null });
      return undefined;
    }

    const controller = new AbortController();
    let mounted = true;
    setBalanceState({ loading: true, error: "", summary: null });

    getMyLoyalty({ signal: controller.signal })
      .then((summary) => {
        if (mounted) {
          setBalanceState({ loading: false, error: "", summary });
        }
      })
      .catch((error) => {
        if (mounted && error?.code !== "ERR_CANCELED") {
          setBalanceState({
            loading: false,
            error: "Не вдалося завантажити ваш бонусний баланс.",
            summary: null,
          });
        }
      });

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [customerId, customerLoading]);

  const view = useMemo(() => {
    if (!settings) return null;
    const regular = ruleView(settings, "regularProducts");
    const sale = ruleView(settings, "saleProducts");
    const maxPercent = settings.redemptionEnabled
      ? Math.max(0, Number(settings.maxRedeemPercent) || 0)
      : 0;
    const regularBonus = regular.enabled
      ? exampleBonus(80000, regular.percent)
      : 0;
    const saleBonus = sale.enabled ? exampleBonus(20000, sale.percent) : 0;
    const redeem = Math.min(50000, exampleBonus(100000, maxPercent));

    return {
      enabled: Boolean(settings.enabled),
      regular,
      sale,
      maxPercent,
      months: Math.max(
        0,
        Math.trunc(Number(settings.expirationMonths) || 0),
      ),
      multiplier: Math.max(0, Number(settings.defaultMultiplier) || 1),
      regularBonus,
      saleBonus,
      totalBonus: regularBonus + saleBonus,
      redeem,
      itemsAfterBonus: 100000 - redeem,
      finalPayment: 108000 - redeem,
    };
  }, [settings]);

  const maxEarnPercent = view
    ? Math.max(
        view.regular.enabled ? view.regular.percent : 0,
        view.sale.enabled ? view.sale.percent : 0,
      )
    : 0;

  const balanceLoading =
    customerLoading || (isAuthenticated && balanceState.loading);
  const balanceKopecks = isAuthenticated
    ? balanceState.summary?.availableBonusKopecks ?? 0
    : DEMO_BALANCE_KOPECKS;

  return (
    <div className={`${styles.page} ${styles.fadeIn}`}>
      <Breadcrumbs
        paths={[
          { label: "Головна", to: "/" },
          { label: "Бонусна програма", to: "/bonus-program" },
        ]}
      />

      <header className={styles.hero}>
        <div className={styles.heroCopy}>
          <span className={styles.eyebrow}>MyZooShop Club</span>
          <h1>Бонусна програма MyZooShop</h1>
          <p className={styles.heroDescription}>
            Купуйте товари для своїх улюбленців, отримуйте бонуси та
            заощаджуйте на наступних покупках.
          </p>
          <p className={styles.heroSubtext}>
            Бонуси нараховуються у гривнях — без балів, коефіцієнтів і складних
            перерахунків.
          </p>

        </div>

        <aside className={styles.heroValueCard} aria-label="Еквівалент бонусів">
          <FiGift aria-hidden="true" />
          <span>Ваша реальна вигода</span>
          <strong>1 грн бонусів</strong>
          <b>= 1 грн знижки</b>
          <small>Просто й без додаткових перерахунків</small>
        </aside>
      </header>

      {state.loading && (
        <div className={styles.message} role="status">
          Завантажуємо актуальні умови…
        </div>
      )}
      {settings?.mode === "maintenance" && <div className={styles.message} role="status">{LOYALTY_MAINTENANCE_MESSAGE}</div>}
      {state.error && (
        <div className={`${styles.message} ${styles.error}`} role="alert">
          <span>{state.error}</span>
          <button type="button" onClick={() => setRetry((value) => value + 1)}>
            <FiRefreshCw aria-hidden="true" /> Повторити
          </button>
        </div>
      )}

      {view && (
        <main>
          <section className={styles.metrics} aria-label="Основні умови програми">
            {[
              [
                FiPercent,
                view.enabled && maxEarnPercent > 0
                  ? `До ${formatPercent(maxEarnPercent)}`
                  : "Призупинено",
                "бонусами за покупки",
              ],
              [FiCreditCard, "1:1", "бонуси та знижка у гривнях"],
              [
                FiShoppingCart,
                `До ${formatPercent(view.maxPercent)}`,
                "вартості товарів бонусами",
              ],
              [FiClock, formatMonths(view.months), "від останньої покупки"],
            ].map(([Icon, value, label]) => (
              <article className={styles.metric} key={`${value}-${label}`}>
                <Icon aria-hidden="true" />
                <div>
                  <strong>{value}</strong>
                  <span>{label}</span>
                </div>
              </article>
            ))}
          </section>

          <section className={`${styles.chapter} ${styles.stepsChapter}`}>
            <SectionHead Icon={FiShoppingCart} id="how-title" title="Як це працює?">
              Три прості кроки — усе інше система зробить автоматично.
            </SectionHead>
            <div className={styles.timeline} aria-labelledby="how-title">
              {[
                [
                  "01",
                  "Купуйте",
                  "Оформлюйте замовлення у MyZooShop як зазвичай.",
                ],
                [
                  "02",
                  "Отримуйте",
                  "Після успішного завершення покупки бонуси стають доступними.",
                ],
                [
                  "03",
                  "Заощаджуйте",
                  "Використовуйте бонуси як знижку в наступному замовленні.",
                ],
              ].map(([number, title, text]) => (
                <article className={styles.timelineStep} key={number}>
                  <span>{number}</span>
                  <div>
                    <h3>{title}</h3>
                    <p>{text}</p>
                  </div>
                </article>
              ))}
            </div>
            <p className={styles.autoNote}>
              <FiCheckCircle aria-hidden="true" /> Нічого активувати вручну не
              потрібно.
            </p>
          </section>

          <section className={styles.chapter} aria-labelledby="earning-title">
            <SectionHead
              Icon={FiAward}
              id="earning-title"
              title="Нарахування бонусів"
            >
              Розмір бонусу залежить від ціни, за якою фактично придбано товар.
            </SectionHead>

            <div className={styles.earningLayout}>
              <div className={styles.rateStack}>
                <article className={styles.rateCard}>
                  <span className={styles.cardKicker}>Звичайна ціна</span>
                  <strong>
                    {view.regular.enabled
                      ? `${formatPercent(view.regular.percent)} бонусами`
                      : "Нарахування вимкнено"}
                  </strong>
                  <p>
                    {view.regular.enabled
                      ? `Від фактично оплаченої вартості товару після знижок.`
                      : "За товари цієї групи бонуси зараз не нараховуються."}
                  </p>
                  <small>
                    800 грн × {formatPercent(view.regular.percent)} ={" "}
                    {formatKopecks(view.regularBonus)}
                  </small>
                </article>

                <article className={styles.rateCard}>
                  <span className={styles.cardKicker}>Акційна ціна</span>
                  <strong>
                    {view.sale.enabled
                      ? `${formatPercent(view.sale.percent)} бонусами`
                      : "Нарахування вимкнено"}
                  </strong>
                  <p>
                    {view.sale.enabled
                      ? "Для товару, придбаного за фактичною акційною ціною."
                      : "За акційні товари бонуси зараз не нараховуються."}
                  </p>
                  <small>
                    200 грн × {formatPercent(view.sale.percent)} ={" "}
                    {formatKopecks(view.saleBonus)}
                  </small>
                </article>
              </div>

              <article className={styles.receipt}>
                <div className={styles.receiptHead}>
                  <div>
                    <span className={styles.cardKicker}>Приклад покупки</span>
                    <h3>Замовлення на 1 000 грн</h3>
                  </div>
                  <FiShoppingCart aria-hidden="true" />
                </div>
                <div className={styles.receiptRows}>
                  <div>
                    <span>Звичайні товари</span>
                    <b>800 грн</b>
                  </div>
                  <div>
                    <span>Нараховано</span>
                    <strong>+{formatKopecks(view.regularBonus)}</strong>
                  </div>
                  <div>
                    <span>Акційні товари</span>
                    <b>200 грн</b>
                  </div>
                  <div>
                    <span>Нараховано</span>
                    <strong>+{formatKopecks(view.saleBonus)}</strong>
                  </div>
                  <div className={styles.receiptTotal}>
                    <span>Разом бонусів</span>
                    <strong>+{formatKopecks(view.totalBonus)}</strong>
                  </div>
                </div>
                <p>
                  Сума буде додана до балансу після успішного завершення
                  замовлення.
                </p>
              </article>
            </div>

            {view.multiplier !== 1 && view.enabled && (
              <div className={styles.promo}>
                <FiGift aria-hidden="true" /> Зараз діє промо-множник ×
                {percentFormatter.format(view.multiplier)}. У ставках уже
                показано підсумковий відсоток.
              </div>
            )}
          </section>

          <section className={styles.chapter} aria-labelledby="activation-title">
            <SectionHead
              Icon={FiClock}
              id="activation-title"
              title="Активація та обмеження"
            >
              Бонуси стають доступними після отримання і успішного завершення
              замовлення.
            </SectionHead>

            <div className={styles.ruleGrid}>
              <article className={`${styles.ruleCard} ${styles.successRule}`}>
                <FiCheckCircle aria-hidden="true" />
                <div>
                  <h3>Бонуси нараховуються</h3>
                  <ul>
                    <li>замовлення отримано;</li>
                    <li>покупка успішно завершена;</li>
                    <li>товари не були повернуті.</li>
                  </ul>
                </div>
              </article>
              <article className={`${styles.ruleCard} ${styles.stopRule}`}>
                <FiXCircle aria-hidden="true" />
                <div>
                  <h3>Бонуси не нараховуються</h3>
                  <ul>
                    <li>замовлення скасовано;</li>
                    <li>покупець відмовився від отримання;</li>
                    <li>замовлення не завершене успішно.</li>
                  </ul>
                </div>
              </article>
            </div>

            <div className={styles.priceNote}>
              <FiCreditCard aria-hidden="true" />
              <p>
                Бонус розраховується від суми, фактично сплаченої грошима після
                знижок. Якщо товар коштував 500 грн, а придбаний за 400 грн,
                бонус нараховується саме від 400 грн.
              </p>
            </div>

            <div className={styles.exclusions} aria-label="Винятки з нарахування">
              {[
                [FiTruck, "Доставка", "Не бере участі в розрахунку"],
                [FiPackage, "Послуги перевізника", "Не враховуються"],
                [FiGift, "Оплачено бонусами", "Нові бонуси не нараховуються"],
              ].map(([Icon, title, text]) => (
                <article key={title}>
                  <Icon aria-hidden="true" />
                  <div>
                    <strong>{title}</strong>
                    <span>{text}</span>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className={styles.chapter} aria-labelledby="using-title">
            <SectionHead
              Icon={FiCreditCard}
              id="using-title"
              title="Використання бонусів"
            >
              Під час оформлення ви побачите баланс, доступний ліміт і суму до
              оплати після бонусної знижки.
            </SectionHead>

            <div className={styles.usingLayout}>
              <article className={styles.balanceCard}>
                <span>Ваш бонусний баланс</span>
                <strong>
                  {balanceLoading
                    ? "Завантаження…"
                    : balanceState.error && isAuthenticated
                      ? "Тимчасово недоступний"
                      : formatKopecks(balanceKopecks)}
                </strong>
                <p>
                  {isAuthenticated
                    ? "Уже доступно для наступної покупки."
                    : "Демонстраційний приклад. Авторизуйтеся, щоб побачити свій баланс."}
                </p>
                <b>До {formatPercent(view.maxPercent)} вартості товарів</b>
                {balanceState.error && isAuthenticated && (
                  <small role="status">{balanceState.error}</small>
                )}
              </article>

              <article className={`${styles.receipt} ${styles.paymentReceipt}`}>
                <div className={styles.receiptHead}>
                  <div>
                    <span className={styles.cardKicker}>Приклад використання</span>
                    <h3>Замовлення на 1 000 грн</h3>
                  </div>
                  <FiPercent aria-hidden="true" />
                </div>
                <div className={styles.receiptRows}>
                  <div>
                    <span>Бонусний баланс</span>
                    <b>{formatKopecks(50000)}</b>
                  </div>
                  <div>
                    <span>Максимальна знижка</span>
                    <strong>−{formatKopecks(view.redeem)}</strong>
                  </div>
                  <div>
                    <span>Товари після знижки</span>
                    <b>{formatKopecks(view.itemsAfterBonus)}</b>
                  </div>
                  <div>
                    <span>Доставка</span>
                    <b>+{formatKopecks(8000)}</b>
                  </div>
                  <div className={styles.receiptTotal}>
                    <span>Разом до оплати</span>
                    <strong>{formatKopecks(view.finalPayment)}</strong>
                  </div>
                </div>
                <p>Вартість доставки не можна оплатити бонусами.</p>
              </article>
            </div>
          </section>

          <section className={styles.chapter} aria-labelledby="lifecycle-title">
            <SectionHead
              Icon={FiRotateCcw}
              id="lifecycle-title"
              title="Строк дії та повернення"
            >
              Система автоматично продовжує строк і перераховує баланс при
              поверненні.
            </SectionHead>

            <div className={styles.lifecycleGrid}>
              <article className={styles.lifecycleCard}>
                <FiClock aria-hidden="true" />
                <span className={styles.cardKicker}>Строк дії</span>
                <strong>{formatMonths(view.months)}</strong>
                <p>
                  Від останньої успішної покупки. Нова покупка продовжує строк
                  дії всього доступного балансу.
                </p>
                <small>Не потрібно стежити за кожним нарахуванням окремо.</small>
              </article>

              <article className={styles.lifecycleCard}>
                <FiRotateCcw aria-hidden="true" />
                <span className={styles.cardKicker}>Повернення товару</span>
                <h3>Баланс перераховується автоматично</h3>
                <p>
                  Нараховані за повернений товар бонуси анулюються пропорційно
                  поверненій частині.
                </p>
                <p>
                  Використана бонусна знижка за повернений товар повертається на
                  бонусний баланс.
                </p>
              </article>
            </div>
          </section>

          <aside className={styles.finalCta}>
            <FiGift aria-hidden="true" />
            <div>
              <h2>Купуйте вигідніше з MyZooShop</h2>
              <p>
                Накопичуйте бонуси та використовуйте їх як знижку на наступні
                замовлення.
              </p>
            </div>
            <strong>50 грн бонусів = 50 грн знижки</strong>
          </aside>
        </main>
      )}
    </div>
  );
};

export default BonusProgram;
